import React from 'react';
import { Button } from '@repo/ui';
import { showToast } from '@repo/ui';
import type { ImageAsset } from '../api';
import { handleImageError, copyToClipboard } from '../utils';

// Standardized button styles
const BUTTON_STYLES = {
  standard: {
    variant: 'outline' as const,
    size: 'sm' as const,
  },
  ghost: {
    variant: 'ghost' as const,
    size: 'sm' as const,
  },
} as const;

const BUTTON_CLASSES = {
  closeButton:
    'h-8 w-8 rounded-md text-xtnd-dark-300 hover:text-xtnd-dark-500 hover:bg-xtnd-dark-50 focus:outline-none focus:ring-2 focus:ring-xtnd-blue focus:ring-offset-2 dark:text-xtnd-dark-200 dark:hover:text-xtnd-dark-50 dark:hover:bg-xtnd-dark-700',
} as const;

interface ImagePreviewModalProps {
  image: ImageAsset | null;
  isOpen: boolean;
  onClose: () => void;
}

export const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({ image, isOpen, onClose }) => {
  if (!isOpen || !image) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-xtnd-dark-800 rounded-lg p-6 max-w-4xl max-h-[90vh] overflow-auto border border-xtnd-dark-100 dark:border-xtnd-dark-600">
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-white">
              {image.originalName}
            </h3>
            <Button
              onClick={onClose}
              {...BUTTON_STYLES.ghost}
              aria-label="Close"
              className={BUTTON_CLASSES.closeButton}
            >
              ✕
            </Button>
          </div>

          <div className="mb-4 bg-xtnd-dark-50 dark:bg-xtnd-dark-700 rounded-lg p-4 border border-xtnd-light dark:border-xtnd-dark-600">
            <img
              src={image.previewUrl || ''}
              alt={image.originalName || 'Image'}
              className="max-w-full max-h-96 mx-auto rounded-lg shadow-lg"
              onError={e => handleImageError(e, image, true)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200">Dimensions:</span>
              <span className="ml-2 text-xtnd-dark-500 dark:text-xtnd-dark-300">
                {image.dimensions?.width || '?'} × {image.dimensions?.height || '?'} pixels
              </span>
            </div>
            <div>
              <span className="font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200">File Size:</span>
              <span className="ml-2 text-xtnd-dark-500 dark:text-xtnd-dark-300">
                {(image.size / 1024).toFixed(1)} KB
              </span>
            </div>
            <div>
              <span className="font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200">Type:</span>
              <span className="ml-2 text-xtnd-dark-500 dark:text-xtnd-dark-300">{image.mimeType}</span>
            </div>
            <div>
              <span className="font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200">Uploaded:</span>
              <span className="ml-2 text-xtnd-dark-500 dark:text-xtnd-dark-300">
                {new Date(image.createdAt).toLocaleDateString()}
              </span>
            </div>
          </div>

          {image.description && (
            <div className="mt-4">
              <span className="font-medium text-xtnd-dark-600">Description:</span>
              <p className="mt-1 text-xtnd-dark-500">{image.description}</p>
            </div>
          )}

          <div className="mt-6 flex items-center justify-between">
            <div className="text-xs text-xtnd-dark-400">Image ID: {image.imageId}</div>
            <div className="space-x-2">
              <Button
                {...BUTTON_STYLES.standard}
                onClick={() =>
                  copyToClipboard(
                    `{{embedImage "${image.imageId}"}}`,
                    showToast,
                    'Template code copied to clipboard!'
                  )
                }
              >
                Copy Template Code
              </Button>
              <Button {...BUTTON_STYLES.standard} onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

